import type { ReactNode } from 'react'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import type { Id } from '@/infra/id'
import styles from './Summary.module.css'
import cx from 'classnames'
import { match } from '@/utils/pattern-match-util'
import { Spinner } from '@/components/Spinner'
import { Tooltip } from '@/components/Tooltip'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import logoTaxfyleWhiteUrl from '@/assets/misc/logo_taxfyle-white.svg'

export interface SummaryJobScopeItem {
  id: Id
  name: string
  quantity: number
}

interface SummaryItemProps {
  item: SummaryJobScopeItem
}

function SummaryItem({ item }: SummaryItemProps) {
  return (
    <div className={styles.item}>
      <div className={styles.itemInfo}>
        <div>{item.name}</div>
        <div>x {item.quantity}</div>
      </div>
    </div>
  )
}

function SummaryDivider() {
  return <div className={styles.divider} />
}

function Logo() {
  return (
    <div className={styles.logo}>
      <img src={logoTaxfyleWhiteUrl} alt="White taxfyle logo." />
    </div>
  )
}

export interface SummaryJobActionsProps {
  canRemoveJob: boolean
  onRemoveJob: () => void
  onViewJob: () => void
}

export function SummaryJobActions({
  canRemoveJob,
  onRemoveJob,
  onViewJob,
}: SummaryJobActionsProps) {
  return (
    <Dropdown
      autoDismiss
      button={<IconButton name="more" label="Job actions" color="white" />}
    >
      <DropdownTitle>Actions</DropdownTitle>

      {canRemoveJob && (
        <DropdownItem onPress={onRemoveJob}>
          <DropdownItemTitle>Remove Job</DropdownItemTitle>
        </DropdownItem>
      )}
      <DropdownItem onPress={onViewJob}>
        <DropdownItemTitle>View Job</DropdownItemTitle>
      </DropdownItem>
    </Dropdown>
  )
}

interface SummaryDetailsProps {
  label: string
  value?: string
}

function SummaryDetails({ label, value }: SummaryDetailsProps) {
  return (
    <div className={styles.subtotal}>
      <div>{label}</div>
      <div>{value}</div>
    </div>
  )
}

export interface SummaryCouponProps {
  name?: string
  description?: string
  discount?: string
  onRemove: () => void
}

export function SummaryCoupon({
  name,
  description,
  discount,
  onRemove,
}: SummaryCouponProps) {
  return (
    <div className={styles.couponSummary}>
      <div className={styles.appliedBadge}>
        <Icon size={12} name="tick" /> Applied
      </div>
      <div>
        <div className={styles.couponName}>{name}</div>
        <div className={styles.couponDescription}>{description}</div>
      </div>
      <div className={styles.couponDiscount}>{discount}</div>
      <RawButton
        className={styles.couponAction}
        aria-label="Remove coupon"
        onPress={onRemove}
      >
        <Icon size={24} name="close" />
      </RawButton>
    </div>
  )
}

export interface SummaryDiscountProps {
  hasCoupons: boolean
  discountLabel?: string
  coupon?: ReactNode
  onApplyCoupon: () => void
}

export function SummaryDiscount({
  hasCoupons,
  discountLabel,
  coupon,
  onApplyCoupon,
}: SummaryDiscountProps) {
  return (
    <>
      {hasCoupons ? (
        <>
          <SummaryDetails label="Discount coupon" value={discountLabel} />
          {coupon}
        </>
      ) : (
        <RawButton
          className={cx(styles.couponAction, styles.discount)}
          onPress={onApplyCoupon}
        >
          Apply discount coupon
        </RawButton>
      )}
    </>
  )
}

export type SummaryJobPaymentStatus =
  | { state: 'pending' }
  | { state: 'rejected'; errorMessage: string }
  | { state: 'resolved' }

export interface SummaryJobProps {
  name: string
  actions: ReactNode
  items: SummaryJobScopeItem[]
  subtotal?: string
  discount: ReactNode
  paymentStatus: SummaryJobPaymentStatus
  isCheckoutCompleted: boolean
  isSpacious: boolean
}

export function SummaryJob({
  name,
  actions,
  items,
  subtotal,
  discount,
  paymentStatus,
  isCheckoutCompleted,
  isSpacious,
}: SummaryJobProps) {
  return (
    <div className={cx(styles.jobSummary, isSpacious && styles.spacious)}>
      <div className={styles.header}>
        <div className={styles.jobName}>{name}</div>
        <div className={styles.more}>{actions}</div>
      </div>
      <div className={styles.items}>
        {items.map((item) => (
          <SummaryItem key={item.id} item={item} />
        ))}
      </div>
      <SummaryDivider />
      <SummaryDetails label="Subtotal" value={subtotal} />
      <SummaryDivider />
      {discount}
      {match(paymentStatus, 'state', {
        pending: () => (
          <div className={styles.loadingContainer}>
            <Spinner size={24} />
          </div>
        ),
        rejected: ({ errorMessage }) => (
          <div className={cx(styles.loadingContainer, styles.error)}>
            <Tooltip content={errorMessage} placement="top">
              <Icon size={22} name="declined" />
            </Tooltip>
          </div>
        ),
        resolved: () => (
          <div
            className={cx(
              styles.loadingContainer,
              isCheckoutCompleted && styles.success,
            )}
          >
            {isCheckoutCompleted && <Icon size={22} name="checkmark" />}
          </div>
        ),
      })}
    </div>
  )
}

export interface SummaryProps {
  isCheckoutCompleted: boolean
  total: string
  children?: ReactNode
}

export function Summary({
  isCheckoutCompleted,
  total,
  children,
}: SummaryProps) {
  return (
    <div className={styles.root}>
      <div className={styles.summaryContainer}>
        <div className={styles.pageTitle}>
          {isCheckoutCompleted ? 'Summary' : 'Checkout'}
        </div>
        <div className={styles.itemList}>
          {children}
          <div className={styles.scrollGradient} />
        </div>
        <div>
          <div className={styles.total}>
            <div>Amount due now!!</div>
            <div>{total}</div>
          </div>
          <SummaryDivider />
          <Logo />
        </div>
      </div>
    </div>
  )
}
